import * as React from 'react';
import styled from 'styled-components';

import { SessionContext } from 'src/Views/SessionContext';

import Button from 'src/SharedComponents/Button';
import Dialog from 'src/SharedComponents/Dialog/Dialog';
import DialogActions from 'src/SharedComponents/Dialog/DialogActions';
import ErrorBox from 'src/SharedComponents/ErrorBox';
import LoadingIndicator from 'src/SharedComponents/LoadingIndicator';
import TextInput from 'src/SharedComponents/TextInput';
import ManagerTitleBar from 'src/Views/Manager/ManagerTitleBar';

import { createRole, getApplications } from './roleManagementService';
import IRole from 'src/ServerEntities/IRole';
import SectionList, { ISectionListData, ISectionListRenderItemInfo } from 'src/SharedComponents/List/SectionList';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Label from 'src/SharedComponents/Label';
import ListItem from 'src/SharedComponents/List/ListItem';
import useEffectOnSome from 'src/CustomHooks/useEffectOnSome';

const UserFieldsContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 16px;
`;

const UserFieldLine = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
`;
const UserFieldCell = styled.div`
    width: 100%;
`;

const StatusContainer = styled.div`
    height: 50px;
    text-align: center;
`;

const MainContainer = styled.div`
    display:flex;
    height: 100%;
    flex-direction: column;
    flex-wrap: nowrap;
    align-content: space-around;
    justify-content: center;
    align-items: center;
`;

interface IProps {
    show: boolean,
    onClose: () => void,
    onConfirmRoleAdd: () => void
}


const EmptyList = () => <div style={{ color: "#555", padding: "24px" }}>There are no more applications to add to this role.</div>

const getSections = (apps: string[]): Array<ISectionListData<{ title: string }>> => [
    { data: apps.map((app: string) => ({ title: app })) }
];

const getComponentSections = (comps: string[]): Array<ISectionListData<{ title: string }>> => [
    { data: comps.map((comp: string) => ({ title: comp })) }
];

const appMatchesApp = (appToMatch: string) => (app: string) => appToMatch === app;
const componentMatchesComponent = (componentToMatch: string) => (component: string) => componentToMatch === component;

const CreateRoleDialog = (props: IProps) => {
    const [appsToAdd, setAppsToAdd] = React.useState([] as string[]);
    const { webToken } = React.useContext(SessionContext).state;
    const [roleValue, setRoleValue] = React.useState("");
    const [descriptionValue, setDescriptionValue] = React.useState("");
    const [applications, setApplications] = React.useState([] as unknown as string[]);
    const [components, setComponents] = React.useState<string[]>([]);
    const [selectedComponents, setSelectedComponents] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState("");
    const [appError, setAppError] = React.useState("");
    const [success, setSuccess] = React.useState(false);
    const [roleToAdd, setRoleToAdd] = React.useState({} as IRole);

    const closeDialog = () => {
        setRoleValue("");
        setDescriptionValue("");
        setAppsToAdd([]);
        setSelectedComponents([]);
        setError("");
        setAppError("");
        if (success) {
            props.onConfirmRoleAdd();
            setSuccess(false);
        }
        props.onClose();
    };

    const addRoleConfirm = () => {
        setError("");
        setAppError("");
        let valid = true;
        
        if (!roleValue || !descriptionValue) {
            setError("Role and description cannot be empty.");
            valid = false;
        }

        if (appsToAdd.length === 0) {
            setAppError("At least one application must be selected.");
            valid = false;
        }

        if (!valid) return;
        
        setLoading(true);
        roleToAdd.id = roleValue;
        roleToAdd.description = descriptionValue;
        roleToAdd.applications = appsToAdd;
        (roleToAdd as any).components = selectedComponents;
        setRoleToAdd(roleToAdd)
        createRole(roleValue, webToken, roleToAdd,
            () => {
                setLoading(false)
                setSuccess(true);
                setError("");
                setAppError("");
            },
            (errorMessage) => {
                setLoading(false);
                setError(errorMessage);
                setSuccess(false);
                setAppError("");
                return ""
            })
    };

    const changeRoleValue = (e: React.SyntheticEvent<HTMLInputElement>) => {
        setRoleValue(e.currentTarget.value);
        if (e.currentTarget.value) setError("");
    };

    const changeDescriptionValue = (e: React.SyntheticEvent<HTMLInputElement>) => {
        setDescriptionValue(e.currentTarget.value);
        if (e.currentTarget.value) setError("");
    };

    const toggleAppToAdd = (app: string) => () => {
        const appsToAddCopy = [...appsToAdd];
        const appIndexInArray = appsToAddCopy.findIndex((appToAdd: string) => {
            return app === appToAdd;
        });
        if (appIndexInArray === -1) {
            appsToAddCopy.push(app);
        } else {
            appsToAddCopy.splice(appIndexInArray, 1);
        }
        setAppsToAdd(appsToAddCopy);
        if (appsToAddCopy.length > 0) setAppError("");
    };

    const toggleComponentToAdd = (component: string) => () => {
        const componentsToAddCopy = [...selectedComponents];
        const componentIndexInArray = componentsToAddCopy.findIndex((c) => c === component);
        if (componentIndexInArray === -1) {
            componentsToAddCopy.push(component);
        } else {
            componentsToAddCopy.splice(componentIndexInArray, 1);
        }
        setSelectedComponents(componentsToAddCopy);
    };

    const renderComponent = (info: ISectionListRenderItemInfo<{ title: string }>) =>
        <ListItem onClick={toggleComponentToAdd(components[info.index])} key={`component-item${info.index}`}>
            <Label>
                <h2>{info.item.title}</h2>
            </Label>
            {selectedComponents.findIndex(componentMatchesComponent(info.item.title)) !== -1 && <FontAwesomeIcon icon={faCheck} style={{ color: "#187fba" }} />}
        </ListItem>;

    const renderUser = (info: ISectionListRenderItemInfo<{ title: string }>) =>
        <ListItem onClick={toggleAppToAdd(applications[info.index])} key={`item${info.index}`}>
            <Label>
                <h2>{info.item.title}</h2>
            </Label>
            {appsToAdd.findIndex(appMatchesApp(info.item.title)) !== -1 && <FontAwesomeIcon icon={faCheck} style={{ color: "#187fba" }} />}
        </ListItem>;

    useEffectOnSome(() => {
        setLoading(true);
        getApplications(webToken, (serverApplications: string[]) => {
            setLoading(false);
            setApplications(serverApplications);
            setError("");
        }, (errorMessage: string) => {
            setLoading(false);
            setApplications([]);
            setError("");
        });
        setComponents([ "Intersystems Monitor", "Rhapsody Monitor", "QVera Monitor"]);
    }, [], [webToken]);

 

    return <Dialog open={props.show} onClose={closeDialog} style={{ width: "650px", height: "680px" }} darkBackground={true}>
        <ManagerTitleBar viewName="Create role" viewDescription="Create a new role." />
        <LoadingIndicator show={loading} type="Linear" />
        {!success && <UserFieldsContainer>
            {error && <ErrorBox>{error}</ErrorBox>}
            <UserFieldLine>
                <TextInput margin="10px 10px 20px 10px" value={roleValue} onChange={changeRoleValue} label="Role" />
            </UserFieldLine>
            <UserFieldLine>
                <UserFieldCell>
                    <TextInput margin="10px 10px 20px 10px" value={descriptionValue} onChange={changeDescriptionValue} label="Description" />
                </UserFieldCell>
            </UserFieldLine>
            <UserFieldLine style={{ margin: "10px 10px 20px 10px" }}>
                <UserFieldCell style={{ width: "40%" }}>
                    Applications
                </UserFieldCell>
                <UserFieldCell>
                    {appError && <ErrorBox>{appError}</ErrorBox>}
                    {applications.length === 0 && <EmptyList />}
                    <SectionList sections={getSections(applications)} renderItem={renderUser} />
                </UserFieldCell>
            </UserFieldLine>
            <UserFieldLine style={{ margin: "10px 10px 5px 10px" }}>
                <UserFieldCell style={{ width: "40%" }}>
                    Components
                </UserFieldCell>
                <UserFieldCell style={{ height: "180px", overflow: "auto" }}>
                    {components.length === 0 && <EmptyList />}
                    <SectionList sections={getComponentSections(components)} renderItem={renderComponent} style={{ overflow: 'hidden' }} /> 
                </UserFieldCell>
            </UserFieldLine>
            <StatusContainer>
                {error && <ErrorBox>{error}</ErrorBox>}
            </StatusContainer>
            <DialogActions>
                <Button outlined={true} onClick={closeDialog}>Cancel</Button>
                <Button onClick={addRoleConfirm} margin="0px 16px 0px 16px" disabled={false}>Confirm</Button>
            </DialogActions>
        </UserFieldsContainer>}
        {success && <MainContainer>
            <StatusContainer>Role created successfully </StatusContainer>
            <Button onClick={closeDialog} margin="0px 16px 0px 16px" disabled={false}>Close</Button>
        </MainContainer>}
    </Dialog>;
};

export default CreateRoleDialog;
